import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { adminApi } from "@/lib/adminApi";
import { Field } from "@/pages/admin/Field";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Upload, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export const NoticeSection = () => {
  const [en, setEn] = useState("");
  const [gu, setGu] = useState("");
  const [active, setActive] = useState(true);
  const [ftEn, setFtEn] = useState("");
  const [ftGu, setFtGu] = useState("");
  const [ftShow, setFtShow] = useState(true);

  useEffect(() => {
    api.importantNotice().then((d) => {
      if (d.value) { setEn(d.value.en || ""); setGu(d.value.gu || ""); }
    });
    api.footerSettings().then((d) => { setFtEn(d.tagline_en || ""); setFtGu(d.tagline_gu || ""); setFtShow(d.show !== false); }).catch(() => {});
  }, []);

  const save = async () => {
    try { await adminApi.updateNotice({ en, gu, active }); toast.success("Important bar updated"); }
    catch { toast.error("Save failed"); }
  };
  const saveFooter = async () => {
    try { await adminApi.updateFooterSettings({ tagline_en: ftEn, tagline_gu: ftGu, show: ftShow }); toast.success("Footer tagline updated"); }
    catch { toast.error("Save failed"); }
  };

  return (
    <div className="max-w-2xl space-y-8">
      <div>
        <h2 className="mb-5 font-serif text-2xl font-semibold text-gmoaa-green">Important Bar (red strip)</h2>
        <div className="space-y-4 rounded-xl border border-gmoaa-border-subtle bg-white p-5">
          <Field label="Notice text (English)" textarea value={en} onChange={setEn} />
          <Field label="Notice text (Gujarati)" textarea value={gu} onChange={setGu} />
          <div className="flex items-center gap-2">
            <Switch checked={active} onCheckedChange={setActive} />
            <Label className="text-sm">Show the important bar on the home page</Label>
          </div>
          <Button onClick={save} className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover">Save</Button>
        </div>
      </div>
      <div>
        <h2 className="mb-5 font-serif text-2xl font-semibold text-gmoaa-green">Footer tagline</h2>
        <div className="space-y-4 rounded-xl border border-gmoaa-border-subtle bg-white p-5">
          <Field label="Tagline (English)" value={ftEn} onChange={setFtEn} testid="footer-tagline-en" />
          <Field label="Tagline (Gujarati)" value={ftGu} onChange={setFtGu} testid="footer-tagline-gu" />
          <div className="flex items-center gap-2">
            <Switch checked={ftShow} onCheckedChange={setFtShow} data-testid="footer-tagline-toggle" />
            <Label className="text-sm">Show the tagline line in the website footer</Label>
          </div>
          <p className="text-xs text-gmoaa-text-muted">Leave both boxes empty to use the built-in default tagline.</p>
          <Button onClick={saveFooter} data-testid="footer-tagline-save" className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover">Save footer</Button>
        </div>
      </div>
      <TreasurerSignature />
    </div>
  );
};

const TreasurerSignature = () => {
  const [sig, setSig] = useState(null);
  const [name, setName] = useState("");
  const [designation, setDesignation] = useState("Treasurer");
  const [uploading, setUploading] = useState(false);
  const load = () => adminApi.receiptSignature().then((d) => { setSig(d); setName(d.name || ""); setDesignation(d.designation || "Treasurer"); });
  useEffect(() => { load(); }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    if (!["image/png", "image/jpeg"].includes(file.type)) { toast.error("Signature must be a PNG or JPG"); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error("Signature must be 2 MB or smaller"); return; }
    setUploading(true);
    try { const d = await adminApi.uploadReceiptSignature(file); setSig(d); toast.success("Signature uploaded"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };
  const remove = async () => { const d = await adminApi.deleteReceiptSignature(); setSig(d); toast.success("Signature removed"); };
  const saveText = async () => {
    try { const d = await adminApi.updateReceiptSignature({ name, designation }); setSig(d); toast.success("Signatory details saved"); }
    catch { toast.error("Save failed"); }
  };

  return (
    <div>
      <h2 className="mb-5 font-serif text-2xl font-semibold text-gmoaa-green">Treasurer signature</h2>
      <div className="space-y-4 rounded-xl border border-gmoaa-border-subtle bg-white p-5" data-testid="signature-card">
        <p className="text-sm text-gmoaa-text-muted">Printed on every receipt PDF, above the signature line. PNG with transparent background works best (JPG allowed), max 2 MB.</p>
        {sig && !sig.uploaded && (
          <div className="flex items-center gap-2 rounded-lg border border-gmoaa-red/40 bg-gmoaa-red-bg p-3 text-sm text-gmoaa-red" data-testid="signature-warning">
            <AlertTriangle size={16} /> Treasurer signature not uploaded
          </div>
        )}
        <div className="flex items-center gap-4">
          <div className="flex h-20 w-48 items-center justify-center overflow-hidden rounded border border-gmoaa-border-subtle bg-gmoaa-surface">
            {sig?.image_data_url ? <img src={sig.image_data_url} alt="Signature" className="max-h-full max-w-full object-contain" data-testid="signature-preview" /> : <span className="text-xs text-gmoaa-text-muted">No signature</span>}
          </div>
          <div className="flex flex-col gap-2">
            <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-gmoaa-green bg-white px-3 py-2 text-xs font-medium text-gmoaa-green hover:bg-gmoaa-green-light">
              <Upload size={14} /> {uploading ? "Uploading…" : (sig?.uploaded ? "Replace signature" : "Upload signature")}
              <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={onFile} disabled={uploading} data-testid="signature-upload-input" />
            </label>
            {sig?.uploaded && <Button size="sm" variant="ghost" className="text-gmoaa-red" onClick={remove} data-testid="signature-remove"><Trash2 size={14} className="mr-1" /> Remove</Button>}
          </div>
        </div>
        <Field label="Signatory name" value={name} onChange={setName} placeholder="e.g. Dr. A. B. Patel" testid="signature-name" />
        <Field label="Designation" value={designation} onChange={setDesignation} placeholder="Treasurer" testid="signature-designation" />
        <Button onClick={saveText} data-testid="signature-save" className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover">Save signatory details</Button>
      </div>
    </div>
  );
};
